import { PageShell } from "@/components/layout/PageShell";
import { TeamBadge } from "@/components/teams/TeamBadge";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { UserAvatar } from "@/components/UserAvatar";
import { useAuth } from "@/contexts/AuthContext";
import { Briefcase, Building2, Mail, Shield, UserRound, Users, User as UserIcon } from "lucide-react";

const Profile = () => {
  const { user } = useAuth();

  if (!user) {
    return null; // Should be protected route anyway
  }

  return (
    <PageShell>
      {/* Header Section with Gradient */}
      <div className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8 border border-border/50 shadow-sm">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <UserIcon className="w-64 h-64 -mt-16 -mr-16" />
        </div>
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
          <UserAvatar
            src={user.avatarUrl}
            name={user.fullName}
            className="h-16 w-16 border-4 border-background shadow-xl"
            fallbackClassName="text-3xl font-bold bg-gradient-to-br from-primary to-primary/60 text-primary-foreground"
          />
          <div className="flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{user.fullName}</h1>
              {user.role && (
                <Badge variant="secondary" className="capitalize">
                  <Shield className="mr-1 h-3 w-3" />
                  {user.role.name}
                </Badge>
              )}
            </div>
            {user.position && (
              <p className="text-sm font-medium text-foreground/70">{user.position}</p>
            )}
            <div className="flex flex-wrap items-center gap-3 text-muted-foreground text-sm">
              <div className="flex items-center gap-1.5">
                <Mail className="h-3.5 w-3.5" />
                <span>{user.email}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Information Card */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>
            Your personal details and account settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Company
              </label>
              <p className="text-base font-medium">{user?.company?.name || "--"}</p>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Briefcase className="h-4 w-4" />
                Position
              </label>
              <p className="text-base font-medium">{user.position || "--"}</p>
            </div>

            {user.manager && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <UserRound className="h-4 w-4" />
                  Reports To
                </label>
                <div className="flex items-center gap-2">
                  <UserAvatar
                    src={user.manager.avatarUrl}
                    name={user.manager.fullName}
                    className="h-6 w-6"
                  />
                  <p className="text-base font-medium">{user.manager.fullName}</p>
                </div>
              </div>
            )}

            {user.teams && user.teams.length > 0 && (
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Teams
                </label>
                <div className="flex flex-wrap gap-2">
                  {user.teams.map((team) => (
                    <TeamBadge key={team.pid} team={team} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </PageShell>
  );
};

export default Profile;
